import React, { useEffect, useState } from 'react';
import { Button, Header, Icon, Image, Modal, Popup, Table } from 'semantic-ui-react';
import { ResourceProps, sortResourceInfos, toResourceProps } from '../../../../../utils/reportResourceUtil';
import ResourceRepo from '../../../../../utils/repository/resourceRepo';
import { RECONCILIATIONS_ALIASES, SchemaNames } from '../../../../../constnats/schemaConstants';
import ImportsRepo from '../../../../../utils/repository/importsRepo';
import Alert from '../../../../../utils/alert';
import Loader from '../../../../../components/loader';
import { useNavigate } from 'react-router-dom';
import { ResourceCategory } from '../../../../../constnats/resourceConstants';
import RouteC from '../../../../../constnats/routeConstants';
import AuthService from '../../../../../service/auth';

/**
 * holds the types of schemas for which no files should be ignored
 */
const MIXED_SOURCE_SCHEMA_TYPES = ['OTHER'];

/**
 * holds the allowed extension types for the files which should be included
 */
const ALLOWED_FILE_EXTENSIONS = ['png'];

function shouldIncludeFile(schemaName, fileName, schemaType, fileExtension) {
    let result = true;

    if (!ALLOWED_FILE_EXTENSIONS.includes(fileExtension)) {
        result = false;
    } else if (schemaName.includes('KPI') || MIXED_SOURCE_SCHEMA_TYPES.includes(schemaType)) {
        result = true;
    } else if (
        RECONCILIATIONS_ALIASES.some((alias) => schemaName.includes(alias)) &&
        RECONCILIATIONS_ALIASES.some((alias) => fileName.includes(alias))
    ) {
        result = true;
    } else {
        result = fileName.includes(schemaName);
    }

    return result;
}

function getResourceMap(resources, schemaName, schemaType) {
    const resourceMap = {};
    for (const resource of resources) {
        const path = resource.path;
        const resourceProps = toResourceProps(path);
        const name = ResourceProps.userFriendlyName(resourceProps, true, true, true);

        if (!shouldIncludeFile(schemaName, name, schemaType, resourceProps.fileExtension)) {
            continue;
        }

        const props = resource.props;
        const year = props.year;
        const quoter = props.quoter;
        const awsUrl = resource.serverUrl + '/' + path;
        if (resourceMap[year] === undefined) {
            resourceMap[year] = {};
        }

        if (resourceMap[year][quoter] === undefined) {
            resourceMap[year][quoter] = [];
        }

        resourceMap[year][quoter].push({
            path,
            name,
            year,
            quoter,
            awsUrl,
            lastModified: resource.lastModified,
            originalOnly: resourceProps.originalOnly,
            fileExtension: resourceProps.fileExtension,
            sourceType: resourceProps.sourceType,
        });
    }

    const years = Object.keys(resourceMap);
    for (let year of years) {
        const quters = Object.keys(resourceMap[year]);
        for (let quoter of quters) {
            resourceMap[year][quoter] = sortResourceInfos(resourceMap[year][quoter]);
        }
    }

    return resourceMap;
}

function ImportDataFromImageModal({ closeModal, schemaId, companyId, schemaName, schemaType }) {
    const [loading, setLoading] = useState(true);
    const [resources, setResources] = useState(null);
    const [isCreatingImport, setIsCreatingImport] = useState(false);
    const navigate = useNavigate();

    const handleCreateImport = (resourcePath, schemaId) => {
        setIsCreatingImport(true);
        ImportsRepo.create({ resourcePath, schemaId, ownerId: AuthService.getUserId() }, (response) => {
            setIsCreatingImport(false);
            navigate(`${RouteC.TABLE_EXTRACTION}${response.data.id}`);
        });
    };

    const refreshResources = () => {
        setLoading(true);
        ResourceRepo.listCompanyResources(companyId, (resources) => {
            setResources(resources);
            setLoading(false);
        });
    };

    useEffect(() => {
        refreshResources();
    }, [companyId]);

    const resourceRows = () => {
        const resourcesMap = getResourceMap(resources, schemaName, schemaType);

        let currenYearIdx;
        return Object.keys(resourcesMap)
            .sort((a, b) => b - a)
            .map((year, yearIdx) => {
                const yearResources = resourcesMap[year];
                return Object.keys(yearResources)
                    .sort((a, b) => b - a)
                    .map((q, qIdx) => {
                        return yearResources[q].map((resource, resourceIdx) => {
                            const quoterReports = yearResources[q];
                            const row = (
                                <Table.Row key={resource.path}>
                                    {currenYearIdx !== yearIdx && (
                                        <>
                                            <Table.Cell
                                                rowSpan={Object.keys(yearResources)
                                                    .map((q) => yearResources[q].length)
                                                    .reduce((a, b) => a + b, 0)}
                                            >
                                                {year}
                                            </Table.Cell>
                                        </>
                                    )}
                                    {resourceIdx === 0 && (
                                        <Table.Cell rowSpan={quoterReports.length}>{resource.quoter}</Table.Cell>
                                    )}
                                    <Table.Cell>
                                        <Popup
                                            className="import__resource__image__popup"
                                            content={<Image src={resource.awsUrl} />}
                                            trigger={
                                                <a href={resource.awsUrl} target="_blank" rel="noopener noreferrer">
                                                    {resource.name}
                                                </a>
                                            }
                                        />
                                    </Table.Cell>
                                    <Table.Cell>
                                        {resource.fileExtension === 'pdf' && (
                                            <a
                                                href={'https://drive.google.com/viewerng/viewer?url=' + resource.awsUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Preview
                                            </a>
                                        )}
                                    </Table.Cell>
                                    <Table.Cell>{resource.originalOnly ? 'Yes' : 'No'}</Table.Cell>
                                    <Table.Cell>{new Date(resource.lastModified).toLocaleString()}</Table.Cell>
                                    <Table.Cell>
                                        <Import
                                            resourcePath={resource.path}
                                            schemaId={schemaId}
                                            handleCreateImport={handleCreateImport}
                                        />
                                    </Table.Cell>
                                </Table.Row>
                            );

                            currenYearIdx = yearIdx;

                            return row;
                        });
                    });
            });
    };

    const content = () => {
        return (
            <div>
                {resources.length === 0 ? (
                    <Header as="h5" textAlign="center">
                        No Uploaded Resources
                    </Header>
                ) : (
                    <Table selectable compact="very">
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell>Year (Original)</Table.HeaderCell>
                                <Table.HeaderCell>Quarter</Table.HeaderCell>
                                <Table.HeaderCell>File Server Link</Table.HeaderCell>
                                <Table.HeaderCell>Google Drive</Table.HeaderCell>
                                <Table.HeaderCell>Has Prev. Year Data</Table.HeaderCell>
                                <Table.HeaderCell>Last Modified</Table.HeaderCell>
                                <Table.HeaderCell>Import Data</Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>{resourceRows()}</Table.Body>
                    </Table>
                )}
            </div>
        );
    };

    return (
        <Modal open={true} onClose={closeModal}>
            <Modal.Header>Choose image for data extraction</Modal.Header>
            <Modal.Content>{loading || isCreatingImport ? <Loader /> : content()}</Modal.Content>
        </Modal>
    );
}

function Import({ resourcePath, schemaId, handleCreateImport }) {
    return (
        <Button size="mini" onClick={() => handleCreateImport(resourcePath, schemaId)}>
            Import
        </Button>
    );
}

export default ImportDataFromImageModal;
